import axios from 'axios';
import { baseURL, generalEndpoint } from '../config';

const API_BASE_URL = process.env.REACT_APP_API_URL || baseURL + '/' + generalEndpoint;

/**
 * Create FIX session for LP
 * @param {string} token - Authentication token
 * @param {number} lpId - Liquidity Provider ID
 * @param {Object} sessionData - FIX session data object
 * @returns {Promise<Object>} The API response
 */
export const createFIXSession = async (token, lpId, sessionData) => {
    const config = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        console.log('API Request - URL:', `${API_BASE_URL}/lps/${lpId}/fix-session`);
        console.log('API Request - Data:', sessionData);
        
        const response = await axios.post(
            `${API_BASE_URL}/lps/${lpId}/fix-session`,
            sessionData,
            config
        );
        
        console.log('API Response:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error creating FIX session:', error);
        throw error.response?.data || error.message;
    }
};

/**
 * Get FIX session for LP
 * @param {string} token - Authentication token
 * @param {number} lpId - Liquidity Provider ID
 * @returns {Promise<Object>} The API response
 */
export const getFIXSession = async (token, lpId) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        console.log('API Request - URL:', `${API_BASE_URL}/lps/${lpId}/fix-session/Fetch`);
        
        const response = await axios.get(
            `${API_BASE_URL}/lps/${lpId}/fix-session/Fetch`,
            config
        );
        
        console.log('API Response:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error fetching FIX session:', error);
        throw error.response?.data || error.message;
    }
};

/**
 * Update FIX session for LP
 * @param {string} token - Authentication token
 * @param {number} lpId - Liquidity Provider ID
 * @param {Object} sessionData - FIX session data object
 * @returns {Promise<Object>} The API response
 */
export const updateFIXSession = async (token, lpId, sessionData) => {
    const config = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        console.log('API Request - URL:', `${API_BASE_URL}/lps/${lpId}/fix-session`);
        console.log('API Request - Data:', sessionData);
        
        // Try PUT method first (most common for updates)
        const response = await axios.put(
            `${API_BASE_URL}/lps/${lpId}/fix-session`,
            sessionData,
            config
        );
        
        console.log('API Response Status:', response.status);
        console.log('API Response Data:', response.data);
        
        // Handle 204 No Content response (successful update with no response body)
        if (response.status === 204) {
            console.log('FIX session updated successfully (204 No Content)');
            return { success: true, message: 'FIX session updated successfully' };
        }
        
        return response.data;
    } catch (error) {
        console.error('Error updating FIX session:', error);
        console.error('Error response:', error.response);
        console.error('Error status:', error.response?.status);
        console.error('Error statusText:', error.response?.statusText);
        console.error('Error data:', error.response?.data);
        
        // Provide more specific error information
        if (error.response?.status === 405) {
            throw new Error(`Method Not Allowed (405): The server does not support the ${error.config?.method?.toUpperCase()} method for this endpoint. Please check the API documentation.`);
        }
        
        throw error.response?.data || error.message;
    }
};

/**
 * Delete FIX session for LP
 * @param {string} token - Authentication token
 * @param {number} lpId - Liquidity Provider ID
 * @param {string} sessionId - Optional session ID to delete specific session
 * @returns {Promise<Object>} The API response
 */
export const deleteFIXSession = async (token, lpId) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        console.log('API Request - URL:', `${API_BASE_URL}/lps/${lpId}/fix-session`);
        
        const response = await axios.delete(
            `${API_BASE_URL}/lps/${lpId}/fix-session`,
            config
        );
        
        console.log('API Response Status:', response.status);
        console.log('API Response Data:', response.data);
        
        // Handle 204 No Content response (successful delete with no response body)
        if (response.status === 204) {
            console.log('FIX session deleted successfully (204 No Content)');
            return { success: true, message: 'FIX session deleted successfully' };
        }
        
        return response.data;
    } catch (error) {
        console.error('Error deleting FIX session:', error);
        console.error('Error response:', error.response);
        console.error('Error status:', error.response?.status);
        console.error('Error statusText:', error.response?.statusText);
        console.error('Error data:', error.response?.data);
        
        // Provide more specific error information
        if (error.response?.status === 404) {
            throw new Error('FIX session not found. It may have already been deleted.');
        }
        
        throw error.response?.data || error.message;
    }
};
